// src/components/ui/TeamCard.js - Fixed version
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Mail, Phone } from 'lucide-react';
import { Amplify } from 'aws-amplify';
import { getUrl } from 'aws-amplify/storage';
import Button from './Button'; // Import the Button component

// Helper function to generate initials as a fallback
const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase();
};

// Helper function to check if Amplify has S3 configuration
const isS3Configured = () => {
  try {
    const config = Amplify.getConfig();
    return !!config?.aws_user_files_s3_bucket; 
  } catch (error) {
    console.log('S3 not configured:', error);
    return false;
  }
};

// Helper function to get S3 image URL
const getS3ImageUrl = async (key) => {
  try {
    // Clean the key - remove leading slash if present
    const cleanKey = key.startsWith('/') ? key.substring(1) : key;
    
    console.log(`Getting S3 URL for key: ${cleanKey}`);
    const result = await getUrl({
      key: cleanKey,
      options: {
        accessLevel: 'guest',
        validateObjectExistence: true,
      }
    });
    
    console.log(`S3 URL retrieved: ${result.url}`);
    return result.url;
  } catch (error) {
    console.error(`Error getting S3 URL for ${key}:`, error);
    throw error;
  }
};

const TeamCard = ({ name, position, bio, image, color = 'primary', index, contact }) => {
  // State for managing image loading
  const [imageSrc, setImageSrc] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Get member ID from name for routing
  const memberId = name.toLowerCase().replace(/\s+/g, '-');

  // Load image when component mounts
  useEffect(() => {
    const loadImage = async () => {
      // Skip if no image provided
      if (!image) {
        console.log('No image provided for:', name);
        setImageError(true);
        return;
      }
      
      // Log Amplify configuration for debugging
      console.log('Amplify config for S3:', {
        bucket: Amplify.getConfig()?.aws_user_files_s3_bucket,
        region: Amplify.getConfig()?.aws_user_files_s3_bucket_region
      });
      
      // Check if S3 is configured
      const s3Configured = isS3Configured();
      console.log(`S3 configured: ${s3Configured} for ${name}`);
      
      try {
        if (s3Configured) {
          // Try to get the S3 URL
          console.log(`Attempting to load ${name}'s image from S3: ${image}`);
          try {
            const url = await getS3ImageUrl(image);
            console.log(`S3 URL set for ${name}: ${url}`);
            setImageSrc(url);
          } catch (s3Error) {
            console.error(`S3 error for ${name}:`, s3Error);
            console.log(`Falling back to local path for ${name}: ${image}`);
            setImageSrc(image);
          }
        } else {
          // Use the local path as fallback
          console.log(`Using local path for ${name}: ${image}`);
          setImageSrc(image);
        }
      } catch (error) {
        console.error(`Error loading ${name}'s image:`, error);
        setImageError(true);
      }
    };
    
    loadImage();
  }, [image, name]);

  // Handle image load success
  const handleImageLoad = () => {
    console.log(`Image loaded successfully for ${name}`);
    setImageLoaded(true);
    setImageError(false);
  };

  // Handle image load failure
  const handleImageError = () => {
    console.error(`Image failed to load for ${name}: ${imageSrc}`);
    setImageError(true);
    setImageLoaded(false);
  };

  return (
    <div 
      className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 transform hover:translate-y-[-5px] transition-all h-full"
      style={{ 
        animationDelay: `${index * 100}ms`,
        opacity: 1,
        animation: `fadeInUp 0.4s ease-out ${index * 0.1}s forwards`
      }}
    >
      <div className={`overflow-hidden h-64 bg-gradient-to-r ${
        color === 'primary' ? 'from-primary to-primary-dark' : 
        color === 'secondary' ? 'from-secondary to-secondary-dark' : 
        'from-accent to-accent-dark'
      }`}>
        {imageSrc && !imageError ? (
          <img 
            src={imageSrc}
            alt={name} 
            className={`h-full w-full object-cover transition duration-500 group-hover:scale-110 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        ) : null}
        
        {/* Display initials if image fails to load or no image is provided */}
        {(!imageSrc || imageError || !imageLoaded) && (
          <div className="h-full w-full flex items-center justify-center">
            <span className="text-6xl font-bold text-white">{getInitials(name)}</span>
          </div>
        )}
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-serif font-bold text-gray-900 mb-1">{name}</h3>
        <p className={`text-${color} font-medium mb-3`}>{position}</p>
        <p className="text-gray-600 mb-4">{bio.length > 150 ? `${bio.substring(0, 150)}...` : bio}</p>
        
        <div className="mt-6 flex space-x-3">
          {contact && contact.linkedin && (
            <a 
              href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 text-gray-600 hover:text-primary rounded-full hover:bg-primary-50 transition-colors"
            >
              <Linkedin className="h-5 w-5" />
            </a>
          )}
          {contact && contact.email && (
            <a 
              href={`mailto:${contact.email}`}
              className="p-2 text-gray-600 hover:text-primary rounded-full hover:bg-primary-50 transition-colors"
            >
              <Mail className="h-5 w-5" />
            </a>
          )}
          {contact && contact.phone && (
            <a 
              href={`tel:${contact.phone}`}
              className="p-2 text-gray-600 hover:text-primary rounded-full hover:bg-primary-50 transition-colors"
            >
              <Phone className="h-5 w-5" />
            </a>
          )}
          {/* If no contact info is provided, don't show any icons */}
          {!contact && (
            <div className="p-2 text-gray-400 italic text-sm">
              Contact details not available
            </div>
          )}
        </div>
        
        {/* Updated to use accent (gold) variant */}
        <Button 
          href={`/team/${memberId}`}
          variant="accent"
          size="md"
          className="mt-4"
        >
          View Profile
        </Button>
      </div>
    </div>
  );
};

export default TeamCard;